import { useCallback, useMemo, useRef, type MouseEvent as ReactMouseEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { fetchPublicSettings } from '@/api/client'
import type { ForecastMonthly } from '@/api/types'
import { formatMoney } from '@/lib/format-money'
import {
  DEFAULT_TOP_CUSTOMERS,
  buildCustomerBarRows,
  resolveTopCustomers,
  type BarRow,
} from './customerRevenueBarRows'

export type CustomerChartSelection = {
  id: string
  name: string
}

interface Props {
  data: ForecastMonthly
  selectedCustomerIds: string[]
  onSelectionChange: (customers: CustomerChartSelection[]) => void
  onClearSelection?: () => void
}

/** Distinct hues (not tint steps) so the stack reads at a glance. */
const SEGMENT = {
  committed: { key: 'committed', label: 'Committed', fill: '#081060' },
  toWeighted: { key: 'toWeighted', label: 'Weighted', fill: '#1a8f6c' },
  toUpside: { key: 'toUpside', label: 'Upside', fill: '#d97706' },
} as const

const LEGEND_ITEMS = [SEGMENT.committed, SEGMENT.toWeighted, SEGMENT.toUpside] as const

function selectionIdsForRow(row: BarRow): string[] {
  return row.memberIds ?? [row.id]
}

function rowIsSelected(row: BarRow, selected: Set<string>): boolean {
  const ids = selectionIdsForRow(row)
  return ids.length > 0 && ids.every((id) => selected.has(id))
}

type TooltipPayload = {
  payload?: BarRow
}

function StackTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean
  payload?: TooltipPayload[]
  currency: string
}) {
  if (!active || !payload?.length) return null
  const row = payload[0]?.payload
  if (!row) return null
  return (
    <div className="customer-bars__tooltip">
      <div className="customer-bars__tooltip-name">{row.customer.customer_name}</div>
      <dl className="customer-bars__tooltip-stack">
        <div>
          <dt>Committed</dt>
          <dd>{formatMoney(row.committed, currency)}</dd>
        </div>
        <div>
          <dt>Weighted</dt>
          <dd>
            {formatMoney(row.weighted, currency)}
            <span className="customer-bars__tooltip-delta">
              {' '}
              (+{formatMoney(row.toWeighted, currency)})
            </span>
          </dd>
        </div>
        <div>
          <dt>Upside</dt>
          <dd>
            {formatMoney(row.upside, currency)}
            <span className="customer-bars__tooltip-delta">
              {' '}
              (+{formatMoney(row.toUpside, currency)})
            </span>
          </dd>
        </div>
      </dl>
    </div>
  )
}

const BAR_ROW_PX = 36
const CHART_MARGIN = { top: 4, right: 80, left: 4, bottom: 4 }

function selectionFromRows(rows: BarRow[]): CustomerChartSelection[] {
  const byId = new Map<string, string>()
  for (const row of rows) {
    if (row.memberIds?.length) {
      for (const id of row.memberIds) {
        byId.set(id, id)
      }
      continue
    }
    byId.set(row.id, row.customer.customer_name)
  }
  return [...byId.entries()].map(([id, name]) => ({ id, name }))
}

type AxisTickProps = {
  x?: string | number
  y?: string | number
  payload?: { value?: string }
}

/** Stacked year totals: committed → +to weighted → +to upside (Recharts). */
export function CustomerRevenueBars({
  data,
  selectedCustomerIds,
  onSelectionChange,
  onClearSelection,
}: Props) {
  const currency = data.currency || 'USD'
  const selectedSet = useMemo(() => new Set(selectedCustomerIds), [selectedCustomerIds])
  const anchorIndexRef = useRef<number | null>(null)

  const settingsQuery = useQuery({
    queryKey: ['config', 'settings'],
    queryFn: fetchPublicSettings,
    staleTime: 60_000,
  })
  const topN = useMemo(
    () =>
      resolveTopCustomers(settingsQuery.data?.settings?.customer_bar_top_n, DEFAULT_TOP_CUSTOMERS),
    [settingsQuery.data?.settings?.customer_bar_top_n],
  )

  const rows = useMemo(() => buildCustomerBarRows(data.customers, topN), [data.customers, topN])

  const portfolio = useMemo(() => {
    return rows.reduce(
      (acc, row) => ({
        committed: acc.committed + row.committed,
        weighted: acc.weighted + row.weighted,
        upside: acc.upside + row.upside,
      }),
      { committed: 0, weighted: 0, upside: 0 },
    )
  }, [rows])

  const chartHeight = Math.max(rows.length * BAR_ROW_PX + 28, 120)

  const applySelection = useCallback(
    (index: number, event: { ctrlKey: boolean; metaKey: boolean; shiftKey: boolean }) => {
      const row = rows[index]
      if (!row) return

      const additive = event.ctrlKey || event.metaKey
      const range = event.shiftKey
      const targetIds = selectionIdsForRow(row)
      const targetSelected = rowIsSelected(row, selectedSet)

      if (range && anchorIndexRef.current !== null) {
        const from = Math.min(anchorIndexRef.current, index)
        const to = Math.max(anchorIndexRef.current, index)
        const ranged = rows.slice(from, to + 1)
        if (additive) {
          const names = new Map(selectedCustomerIds.map((id) => [id, id] as const))
          for (const item of ranged) {
            for (const id of selectionIdsForRow(item)) {
              names.set(id, item.memberIds ? id : item.customer.customer_name)
            }
          }
          onSelectionChange([...names.entries()].map(([id, name]) => ({ id, name })))
        } else {
          onSelectionChange(selectionFromRows(ranged))
        }
        return
      }

      anchorIndexRef.current = index

      if (additive) {
        if (targetSelected) {
          const drop = new Set(targetIds)
          onSelectionChange(
            selectedCustomerIds.filter((id) => !drop.has(id)).map((id) => ({ id, name: id })),
          )
        } else {
          const next = new Map(selectedCustomerIds.map((id) => [id, id] as const))
          for (const id of targetIds) {
            next.set(id, row.memberIds ? id : row.customer.customer_name)
          }
          onSelectionChange([...next.entries()].map(([id, name]) => ({ id, name })))
        }
        return
      }

      // Plain click: select only this bar (toggle off if it is already the full selection).
      const onlyThis =
        targetIds.length === selectedCustomerIds.length &&
        targetIds.every((id) => selectedSet.has(id))
      if (onlyThis) {
        onSelectionChange([])
        return
      }
      onSelectionChange(
        targetIds.map((id) => ({
          id,
          name: row.memberIds ? id : row.customer.customer_name,
        })),
      )
    },
    [onSelectionChange, rows, selectedCustomerIds, selectedSet],
  )

  const onBarClick = useCallback(
    (
      item: { payload?: BarRow; originalDataIndex?: number },
      _index: number,
      event: ReactMouseEvent<SVGPathElement>,
    ) => {
      event.preventDefault()
      const row = item.payload
      if (!row) return
      const rowIndex =
        typeof item.originalDataIndex === 'number'
          ? item.originalDataIndex
          : rows.findIndex((r) => r.id === row.id)
      if (rowIndex < 0) return
      applySelection(rowIndex, event)
    },
    [applySelection, rows],
  )

  const renderYTick = useCallback(
    (props: AxisTickProps) => {
      const x = Number(props.x ?? 0)
      const y = Number(props.y ?? 0)
      const label = props.payload?.value ?? ''
      const rowIndex = rows.findIndex((r) => r.name === label)
      const row = rowIndex >= 0 ? rows[rowIndex] : undefined
      const selected = row ? rowIsSelected(row, selectedSet) : false
      return (
        <text
          x={x}
          y={y}
          dy={4}
          textAnchor="end"
          className={
            selected ? 'customer-bars__tick customer-bars__tick--selected' : 'customer-bars__tick'
          }
          fill={selected ? 'var(--colour-primary)' : 'var(--colour-text)'}
          fontSize={12}
          fontWeight={selected ? 800 : 600}
          style={{ cursor: 'pointer' }}
          onClick={(event) => {
            event.preventDefault()
            event.stopPropagation()
            if (rowIndex < 0) return
            applySelection(rowIndex, event)
          }}
        >
          {selected ? `▸ ${label}` : label}
        </text>
      )
    },
    [applySelection, rows, selectedSet],
  )

  const selectedCount = selectedCustomerIds.length

  return (
    <div className="customer-bars">
      <header className="customer-bars__header">
        <div className="customer-bars__copy">
          <h3 className="customer-bars__title">Customer revenue</h3>
          <p className="customer-bars__hint">
            {data.year} year total · top {topN} by upside · rest as Others · stacked committed →
            weighted → upside
            {portfolio.upside > 0 ? ` · upside ${formatMoney(portfolio.upside, currency)}` : ''}
            {' · '}
            click to select · Ctrl/⌘ click to add · Shift click for a range
            {selectedCount > 0 && onClearSelection ? (
              <>
                {' · '}
                <button type="button" className="customer-bars__clear" onClick={onClearSelection}>
                  Clear {selectedCount === 1 ? 'filter' : `${selectedCount} selected`}
                </button>
              </>
            ) : null}
          </p>
        </div>
        <ul className="customer-bars__legend" aria-label="Stack layers">
          {LEGEND_ITEMS.map((item) => (
            <li key={item.key} className="customer-bars__legend-item">
              <span
                className="customer-bars__legend-swatch"
                style={{ background: item.fill }}
                aria-hidden
              />
              {item.label}
            </li>
          ))}
        </ul>
      </header>

      {rows.length === 0 ? (
        <p className="customer-bars__empty">No customer revenue for this year yet.</p>
      ) : (
        <div
          className="customer-bars__frame"
          role="listbox"
          aria-multiselectable="true"
          aria-label={`Stacked committed, weighted, and upside revenue by customer for ${data.year}`}
        >
          <ResponsiveContainer width="100%" height={chartHeight}>
            <BarChart
              data={rows}
              layout="vertical"
              margin={CHART_MARGIN}
              barCategoryGap={8}
              accessibilityLayer
            >
              <CartesianGrid
                horizontal={false}
                stroke="var(--colour-border)"
                strokeDasharray="3 3"
              />
              <XAxis
                type="number"
                tickFormatter={(v: number) => formatMoney(v, currency)}
                tick={{ fill: 'var(--colour-text-muted)', fontSize: 11 }}
                axisLine={{ stroke: 'var(--colour-border)' }}
                tickLine={false}
              />
              <YAxis
                type="category"
                dataKey="name"
                width={136}
                tick={renderYTick}
                axisLine={false}
                tickLine={false}
                interval={0}
              />
              <Tooltip
                cursor={{ fill: 'var(--colour-primary-soft)', opacity: 0.55 }}
                content={<StackTooltip currency={currency} />}
              />
              <Bar
                name={SEGMENT.committed.label}
                dataKey={SEGMENT.committed.key}
                stackId="revenue"
                fill={SEGMENT.committed.fill}
                isAnimationActive={false}
                maxBarSize={22}
                cursor="pointer"
                onClick={onBarClick}
              />
              <Bar
                name={SEGMENT.toWeighted.label}
                dataKey={SEGMENT.toWeighted.key}
                stackId="revenue"
                fill={SEGMENT.toWeighted.fill}
                isAnimationActive={false}
                maxBarSize={22}
                cursor="pointer"
                onClick={onBarClick}
              />
              <Bar
                name={SEGMENT.toUpside.label}
                dataKey={SEGMENT.toUpside.key}
                stackId="revenue"
                fill={SEGMENT.toUpside.fill}
                isAnimationActive={false}
                radius={[0, 4, 4, 0]}
                maxBarSize={22}
                cursor="pointer"
                onClick={onBarClick}
              >
                <LabelList
                  dataKey="upside"
                  position="right"
                  formatter={(v) => formatMoney(Number(v), currency)}
                  style={{
                    fill: 'var(--colour-text-muted)',
                    fontSize: 11,
                    fontWeight: 650,
                  }}
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  )
}
